import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tenantsApi, type Tenant } from '../api/tenants-api';
import '../dashboard/superadmin-dashboard.css';
import './clients.css';

/**
 * ClientsList - Lista de clientes/tenants (SuperAdmin)
 *
 * @remarks
 * Muestra todos los clientes/municipios registrados en el sistema.
 * Usa el mismo diseño que las tablas de proyectos y usuarios.
 * Solo accesible para SuperAdmin.
 */
export function ClientsList(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const [clients, setClients] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  /**
   * Carga la lista de clientes al montar el componente
   */
  useEffect(() => {
    void loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await tenantsApi.getAll();
      setClients(data);
    } catch (err: any) {
      console.error('Error cargando clientes:', err);
      setError(t('clients.errorLoading') || 'Error al cargar clientes');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Navega al formulario de creación
   */
  const handleCreate = () => {
    navigate('/admin/clients/new');
  };

  /**
   * Navega al formulario de edición
   */
  const handleEdit = (client: Tenant) => {
    navigate(`/admin/clients/${client.id}/edit`);
  };

  /**
   * Elimina un cliente con confirmación
   */
  const handleDelete = (clientId: string) => {
    if (deletingId === clientId) {
      // Confirmación - ejecutar delete
      void deleteClient(clientId);
      setDeletingId(null);
    } else {
      // Primera vez - pedir confirmación
      setDeletingId(clientId);
      setTimeout(() => setDeletingId(null), 3000); // Reset después de 3s
    }
  };

  const deleteClient = async (clientId: string) => {
    try {
      await tenantsApi.delete(clientId);
      await loadClients();
    } catch (err: any) {
      console.error('Error eliminando cliente:', err);
      setError(t('clients.errorDeleting') || 'Error al eliminar el cliente');
    }
  };

  /**
   * Helpers para obtener datos del contactInfo
   */
  const getCountry = (client: Tenant): string => {
    return (client as any).contactInfo?.country || '-';
  };

  const getPhone = (client: Tenant): string => {
    return (client as any).contactInfo?.phone || '-';
  };

  if (isLoading) {
    return (
      <div className="table-loading">
        <div className="loading-skeleton" />
        <div className="loading-skeleton" />
        <div className="loading-skeleton" />
      </div>
    );
  }

  return (
    <div className="dashboard-table-container">
      <div className="dashboard-table-header">
        <h2 className="table-title">{t('clients.title') || 'Organizaciones'}</h2>
        <button
          type="button"
          className="btn-create"
          onClick={handleCreate}
        >
          {t('clients.create') || '+ Crear Organización'}
        </button>
      </div>

      {error && (
        <div className="form-error" style={{ margin: 'var(--space-5)' }}>
          {error}
        </div>
      )}

      {clients.length === 0 ? (
        <div className="table-empty">
          <p>{t('clients.empty') || 'No hay organizaciones registradas'}</p>
          <button type="button" className="btn-secondary" onClick={handleCreate}>
            {t('clients.createFirst') || 'Crear primera organización'}
          </button>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="dashboard-table">
            <thead className="table-head">
              <tr>
                <th>N° Org.</th>
                <th>{t('clients.table.name') || 'Nombre'}</th>
                <th>{t('clients.table.slug') || 'Slug'}</th>
                <th>{t('clients.table.country') || 'País'}</th>
                <th>{t('clients.table.phone') || 'Teléfono'}</th>
                <th>{t('clients.table.createdAt') || 'Creado'}</th>
                <th className="table-actions-header">{t('clients.table.actions') || 'Acciones'}</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {clients.map((client) => {
                const isDeleting = deletingId === client.id;

                return (
                  <tr key={client.id} className="table-row">
                    <td className="table-cell-client-number">
                      <span className="client-number-badge">
                        {client.clientNumber
                          ? client.clientNumber.toString().padStart(4, '0')
                          : '-'}
                      </span>
                    </td>
                    <td className="table-cell-name">{client.name}</td>
                    <td className="table-cell-slug">
                      <code className="slug-badge">{client.slug}</code>
                    </td>
                    <td className="table-cell-country">{getCountry(client)}</td>
                    <td className="table-cell-phone">{getPhone(client)}</td>
                    <td className="table-cell-date">
                      {new Date(client.createdAt).toLocaleDateString('es-AR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })}
                    </td>
                    <td className="table-cell-actions">
                      <button
                        type="button"
                        className="btn-table-action btn-edit"
                        onClick={() => handleEdit(client)}
                        title={t('clients.editTooltip') || 'Editar cliente'}
                      >
                        {t('clients.edit') || 'Editar'}
                      </button>
                      <button
                        type="button"
                        className={`btn-table-action btn-delete ${isDeleting ? 'confirming' : ''}`}
                        onClick={() => handleDelete(client.id)}
                        title={isDeleting ? t('clients.confirmTooltip') || 'Click para confirmar' : t('clients.deleteTooltip') || 'Eliminar cliente'}
                      >
                        {isDeleting ? t('clients.confirm') || 'Confirmar' : t('clients.delete') || 'Eliminar'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
